import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { FiX } from 'react-icons/fi';
import { StepType } from './StepType';
import { StepBug } from './StepBug';
import { StepSuggestion } from './StepSuggestion';
import { StepReview } from './StepReview';

interface ClosedBetaWizardProps {
  isOpen: boolean;
  onClose: () => void;
}

export type FeedbackType = 'BUG' | 'SUGGESTION';

export interface BugData {
  type: 'BUG';
  category: string;
  severity: string;
  title: string;
  steps: string[];
  expected: string;
  actual: string;
  context?: string;
}

export interface SuggestionData {
  type: 'SUGGESTION';
  title: string;
  suggestion: string;
  impact: string;
  context?: string;
}

export type FeedbackData = BugData | SuggestionData;

interface Environment {
  url: string;
  userAgent: string;
  viewport: {
    width: number;
    height: number;
  };
  appVersion?: string;
}

export const ClosedBetaWizard: React.FC<ClosedBetaWizardProps> = ({ isOpen, onClose }) => {
  const [currentStep, setCurrentStep] = useState<'type' | 'form' | 'review' | 'success'>('type');
  const [feedbackType, setFeedbackType] = useState<FeedbackType | null>(null);
  const [feedbackData, setFeedbackData] = useState<FeedbackData | null>(null);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Auto-capture environment data
  const getEnvironment = (): Environment => {
    return {
      url: window.location.href,
      userAgent: navigator.userAgent,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight
      },
      appVersion: (import.meta as any).env?.VITE_COMMIT_SHA
    };
  };

  // Base API URL (trim trailing slashes). Falls back to relative paths if unset (dev with proxy/rewrite).
  const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/+$/, '');

  const handleClose = () => {
    // Reset wizard state
    setCurrentStep('type');
    setFeedbackType(null);
    setFeedbackData(null);
    setAttachments([]);
    setError(null);
    setIsSubmitting(false);
    onClose();
  };

  const handleTypeSelect = (type: FeedbackType) => {
    setFeedbackType(type);
    setCurrentStep('form');
  };

  const handleFormSubmit = (data: FeedbackData) => {
    setFeedbackData(data);
    setCurrentStep('review');
  };

  const handleBack = () => {
    if (currentStep === 'form') {
      setCurrentStep('type');
    } else if (currentStep === 'review') {
      setCurrentStep('form');
    }
  };

  const handleFinalSubmit = async () => {
    if (!feedbackData) return;

    setIsSubmitting(true);
    setError(null);

    try {
      // Prepare payload with environment data
      const payload = {
        ...feedbackData,
        environment: getEnvironment()
      };

      // Submit feedback
      const response = await fetch(API_BASE_URL ? `${API_BASE_URL}/beta-feedback` : '/beta-feedback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || 'Failed to submit feedback');
      }

      const result = await response.json();
      const feedbackId = result.id;

      // Upload attachments if any
      if (attachments.length > 0) {
        const formData = new FormData();
        attachments.forEach(file => {
          formData.append('attachments', file);
        });

        const attachmentResponse = await fetch(API_BASE_URL ? `${API_BASE_URL}/beta-feedback/${feedbackId}/attachments` : `/beta-feedback/${feedbackId}/attachments`, {
          method: 'POST',
          headers: {
          },
          credentials: 'include',
          body: formData
        });

        if (!attachmentResponse.ok) {
          console.warn('Failed to upload some attachments');
        }
      }

      setCurrentStep('success');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const renderStep = () => {
    switch (currentStep) {
      case 'type':
        return <StepType onSelect={handleTypeSelect} />;
      case 'form':
        if (feedbackType === 'BUG') {
          return (
            <StepBug
              initialData={feedbackData as BugData}
              onSubmit={handleFormSubmit}
              onBack={handleBack}
              attachments={attachments}
              onAttachmentsChange={setAttachments}
            />
          );
        } else {
          return (
            <StepSuggestion
              initialData={feedbackData as SuggestionData}
              onSubmit={handleFormSubmit}
              onBack={handleBack}
              attachments={attachments}
              onAttachmentsChange={setAttachments}
            />
          );
        }
      case 'review':
        return (
          <StepReview
            feedbackData={feedbackData!}
            attachments={attachments}
            onSubmit={handleFinalSubmit}
            onBack={handleBack}
            isSubmitting={isSubmitting}
            error={error}
          />
        );
      case 'success':
        return (
          <div className="text-center py-8">
            <div className="mx-auto w-12 h-12 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mb-4">
              <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
              Thank you for your feedback!
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Your {feedbackType?.toLowerCase() === 'bug' ? 'bug report' : 'feature suggestion'} has been submitted successfully.
            </p>
            <button
              onClick={handleClose}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
            >
              Close
            </button>
          </div>
        );
      default:
        return null;
    }
  };

  const getStepTitle = () => {
    switch (currentStep) {
      case 'type':
        return 'Select Feedback Type';
      case 'form':
        return feedbackType === 'BUG' ? 'Report a Bug' : 'Suggest a Feature';
      case 'review':
        return 'Review & Submit';
      case 'success':
        return 'Success!';
      default:
        return '';
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-50" 
        onClick={currentStep !== 'success' ? handleClose : undefined}
      />
      
      {/* Modal */}
      <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            {getStepTitle()}
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            aria-label="Close modal"
          >
            <FiX size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-8rem)]">
          {renderStep()}
        </div>
      </div>
    </div>,
    document.body
  );
}; 